import { useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import { type UseFormReturn } from "react-hook-form";
import { Label } from "~/atoms/label";
import { Badge } from "~/atoms/badge";
import { cn } from "~/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type DocumentEditFormValues } from "~/hooks/useDocumentEditForm";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

type LegalEntity = {
  id: string;
  name: string;
  displayName: string;
};

const LEGAL_ENTITIES: LegalEntity[] = [
  {
    id: "if-pc-insurance-ltd",
    name: "If P&C Insurance Ltd (publ)",
    displayName: "If P&C Insurance Ltd (publ)",
  },
  {
    id: "if-livforsakring-ab",
    name: "If Livförsäkring AB",
    displayName: "If Livförsäkring AB",
  },
  {
    id: "if-services-ab",
    name: "If Services AB",
    displayName: "If Services AB",
  },
  {
    id: "viking-assistance-group-as",
    name: "Viking Assistance Group AS",
    displayName: "Viking Assistance Group AS",
  },
  {
    id: "if-pc-insurance-as",
    name: "If P&C Insurance AS",
    displayName: "If P&C Insurance AS",
  },
  {
    id: "topdanmark-as",
    name: "Topdanmark A/S",
    displayName: "Topdanmark A/S",
  },
];

const ENTITY_MAP: Record<string, LegalEntity> = LEGAL_ENTITIES.reduce(
  (acc, entity) => {
    acc[entity.id] = entity;
    return acc;
  },
  {} as Record<string, LegalEntity>,
);

const getEntityDisplayName = (entityId: string): string => {
  return ENTITY_MAP[entityId]?.displayName ?? entityId;
};

const STORAGE_KEY = "demo-document-entities";

const useLegalEntity = (documentId?: number) => {
  const [lastDocumentId, setLastDocumentId] = useState<number | undefined>(
    undefined,
  );
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  // Compute whether this is a programmatic update - true when documentId just changed
  const isProgrammaticUpdate =
    documentId !== undefined && documentId !== lastDocumentId;

  // Initialize or reset state when documentId changes
  if (documentId && documentId !== lastDocumentId) {
    setLastDocumentId(documentId);
    const entityIds = getDocumentEntity(documentId);
    setSelectedEntityIds(entityIds);
  }

  const handleEntityChange = (entityId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedEntityIds((prev) => {
        const newIds = [...prev, entityId];
        // Save immediately with the new entities
        if (documentId) {
          setDocumentEntity(documentId, newIds);
        }
        return newIds;
      });
    } else {
      setSelectedEntityIds((prev) => {
        const newIds = prev.filter((id) => id !== entityId);
        // Save immediately with the new entities
        if (documentId) {
          setDocumentEntity(documentId, newIds);
        }
        return newIds;
      });
    }
  };

  const saveEntityToStorage = (entitiesToSave?: string[]) => {
    if (documentId) {
      const entities = entitiesToSave ?? selectedEntityIds;
      setDocumentEntity(documentId, entities);
    }
  };

  return {
    selectedEntityIds,
    setSelectedEntityIds,
    handleEntityChange,
    saveEntityToStorage,
    isProgrammaticUpdate,
  };
};

const getDocumentEntity = (documentId: number): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const assignments = stored
      ? (JSON.parse(stored) as Record<number, string[] | string>)
      : {};
    const entityData = assignments[documentId];

    if (typeof entityData === "string") {
      return entityData ? [entityData] : [];
    }

    return Array.isArray(entityData) ? entityData : [];
  } catch {
    return [];
  }
};

const setDocumentEntity = (documentId: number, entityIds: string[]): void => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const assignments = stored
      ? (JSON.parse(stored) as Record<number, string[] | string>)
      : {};
    assignments[documentId] = entityIds;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  } catch {
    // Silently fail for demo
  }
};

// Components
interface LegalEntityBadgeProps {
  entityId: string;
  isSelected?: boolean;
  isClickable?: boolean;
  onClick?: () => void;
}

const LegalEntityBadge = ({
  entityId,
  isSelected = false,
  isClickable = false,
  onClick,
}: LegalEntityBadgeProps) => {
  if (!entityId) return null;

  const handleClick = () => {
    if (isClickable && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Badge
      variant="secondary"
      className={cn(
        "px-2 py-0.5 text-xs font-medium transition-all duration-200",
        "focus:ring-0 focus:ring-transparent focus:ring-offset-0 focus:outline-hidden",
        isClickable && "cursor-pointer select-none",
        isClickable && !isSelected && "hover:bg-blue-50",
        isSelected
          ? "border-2 border-[#0054f0] bg-[#0054f0] text-white"
          : isClickable
            ? "border-2 border-[#0054f0] bg-white text-[#0054f0]"
            : "bg-[#0054f0] text-white",
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? "button" : undefined}
      aria-pressed={isClickable ? isSelected : undefined}
    >
      {getEntityDisplayName(entityId)}
    </Badge>
  );
};

interface LegalEntitySelectorProps {
  form: UseFormReturn<DocumentEditFormValues>;
  documentId?: number;
  onProgrammaticChange?: (isProgrammatic: boolean) => void;
}

interface LegalEntitySelectorWrapperProps {
  form: UseFormReturn<DocumentEditFormValues>;
  documentId?: number;
}

interface LegalEntitySelectorRef {
  saveToStorage: () => void;
}

const LegalEntitySelector = forwardRef<
  LegalEntitySelectorRef,
  LegalEntitySelectorProps
>(function LegalEntitySelector(
  { form: _form, documentId, onProgrammaticChange },
  ref,
) {
  const { t } = useTranslation();
  const {
    selectedEntityIds,
    handleEntityChange,
    saveEntityToStorage,
    isProgrammaticUpdate,
  } = useLegalEntity(documentId);

  const availableLegalEntities = LEGAL_ENTITIES;

  useImperativeHandle(ref, () => ({
    saveToStorage: saveEntityToStorage,
  }));

  useEffect(() => {
    if (onProgrammaticChange) {
      onProgrammaticChange(isProgrammaticUpdate);
    }
  }, [selectedEntityIds, onProgrammaticChange, isProgrammaticUpdate]);

  return (
    <>
      <div className="border-t border-gray-200"></div>
      <div className="space-y-4">
        <div className="text-base font-semibold text-gray-800">
          {t(($) => $.legalEntities.legalEntities)}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t(($) => $.legalEntities.selectEntities)}
          </Label>
          <div className="flex flex-wrap gap-3">
            {availableLegalEntities.map((entity) => (
              <LegalEntityBadge
                key={entity.id}
                entityId={entity.id}
                isSelected={selectedEntityIds.includes(entity.id)}
                isClickable={true}
                onClick={() => {
                  const isCurrentlySelected = selectedEntityIds.includes(
                    entity.id,
                  );
                  handleEntityChange(entity.id, !isCurrentlySelected);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
});

// Wrapper components that handle feature flag internally
const LegalEntitySelectorWrapper = ({
  form,
  documentId,
}: LegalEntitySelectorWrapperProps) => {
  const isFeatureEnabled = useFeatureFlagEnabled(featureFlags.legalEntities);

  if (!isFeatureEnabled) {
    return null;
  }

  return <LegalEntitySelector form={form} documentId={documentId} />;
};

const LegalEntityDisplayWrapper = ({ documentId }: { documentId?: number }) => {
  const { t } = useTranslation();
  const isFeatureEnabled = useFeatureFlagEnabled(featureFlags.legalEntities);
  const { selectedEntityIds } = useLegalEntity(documentId);

  if (!isFeatureEnabled || selectedEntityIds.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      <Label variant="muted">{t(($) => $.legalEntities.legalEntity)}</Label>
      <div className="text-sm">
        <div className="flex flex-wrap gap-2">
          {selectedEntityIds.map((entityId) => (
            <LegalEntityBadge key={entityId} entityId={entityId} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Hook to add legal entity filter to existing filters
export const useEnhancedFilters = <T,>(
  baseFilters: Array<{
    id: string;
    label: string;
    options: { value: string; label: string }[];
    getValue: (item: T) => string | string[];
  }>,
  getDocumentId: (item: T) => number,
) => {
  const { t } = useTranslation();
  const isFeatureEnabled = useFeatureFlagEnabled(featureFlags.legalEntities);

  return useMemo(() => {
    // If feature is not enabled or no base filters, return base filters
    if (!isFeatureEnabled || !baseFilters) {
      return baseFilters;
    }

    const legalEntityFilter = {
      id: "legalEntity",
      label: t(($) => $.legalEntities.legalEntity),
      options: LEGAL_ENTITIES.map((entity) => ({
        label: entity.displayName,
        value: entity.id,
      })),
      getValue: (item: T) => {
        const documentId = getDocumentId(item);
        const entityIds = getDocumentEntity(documentId);
        return entityIds;
      },
    };

    return [...baseFilters, legalEntityFilter];
  }, [isFeatureEnabled, baseFilters, getDocumentId, t]);
};

// Export wrapper components for simpler integration
export { LegalEntitySelectorWrapper, LegalEntityDisplayWrapper };
